// utils/conversionRecipes.js
//
// The Quantity Calculator's "Conversion helper": pure maths behind its Convert and Geometry tabs.
//
// Every Nezo material is measured in m³, m², m or No. (never kg / t), so a "conversion" always turns
// something the user knows — a mass, litres, an item size, a %, a part missing from the model — into
// the LINE ITEM's own unit, per 1 unit of the composite. Each recipe returns calculator rows whose
// cells hold the working as formulas (e.g. Factor "=120/7850"), so reviewers can see and edit it.

import { evaluate, isUnit, unit as mathUnit } from 'mathjs';
import { evaluateCell } from './quantityLinks';

// ─── Units ──────────────────────────────────────────────────────────────────────

const UNIT_INFO = {
  volume: { kind: 'volume', label: 'm³', mathjs: 'm^3' },
  area:   { kind: 'area',   label: 'm²', mathjs: 'm^2' },
  length: { kind: 'length', label: 'm',  mathjs: 'm' },
  count:  { kind: 'count',  label: 'No.', mathjs: null },
};

/** A material / composite unit string (CubicMeters, m², Count…) → its kind, or null if unknown. */
export function unitKind(unit) {
  const u = String(unit || '').trim().toLowerCase().replace(/\s+/g, '');
  if (!u) return null;
  if (/^(cubic|m3|m³|cum|cu\.?m)/.test(u)) return 'volume';
  if (/^(square|m2|m²|sqm|sq\.?m)/.test(u)) return 'area';
  if (/^(count|each|ea|no\.?|nr|number|item)/.test(u)) return 'count';
  if (/^(linear|lineal|lm|meters?|metres?|m)$/.test(u) || /^(linear|lineal)/.test(u)) return 'length';
  return null;
}

export const unitInfo = (unit) => UNIT_INFO[unitKind(unit)] || null;

/** The material's density in kg per its OWN unit, from the line item's Weight value (null if none). */
export function densityFromWeight(weight, materialUnit) {
  const v = Number(weight?.value);
  const info = unitInfo(materialUnit);
  if (!info || !Number.isFinite(v) || v <= 0) return null;
  return { value: v, label: `kg per ${info.label}` };
}

/**
 * The weight of `quantity` of the material, using the SAME vquest-Weight value (kg per the material's
 * own unit) that densityFromWeight reads — not a new calculation. Shown in t at or above 1000 kg, else
 * kg. Returns null when there's no Weight on the line item or the quantity isn't a number.
 *
 * `quantity` must be in the material's OWN unit (m³/m²/m/No.) for kg-per-unit × quantity to hold; the
 * caller only shows this when the helper's result is in that unit.
 */
export function weightTotal(quantity, weight, materialUnit) {
  const density = densityFromWeight(weight, materialUnit);
  const q = Number(quantity);
  if (!density || !Number.isFinite(q)) return null;
  const kg = q * density.value;
  const asTonnes = Math.abs(kg) >= 1000;
  return {
    kg,
    value: asTonnes ? kg / 1000 : kg,
    unit: asTonnes ? 't' : 'kg',
    display: `${fmt(asTonnes ? kg / 1000 : kg)} ${asTonnes ? 't' : 'kg'}`,
    perUnit: density.value,
  };
}

/** Typical densities (kg/m³) for the Mass tab's quick picks — editable, never authoritative. */
export const TYPICAL_DENSITIES = [
  { label: 'Concrete', value: 2400 },
  { label: 'Steel', value: 7850 },
  { label: 'Aluminium', value: 2700 },
  { label: 'Timber (softwood)', value: 500 },
  { label: 'Timber (hardwood)', value: 800 },
  { label: 'Glass', value: 2500 },
  { label: 'Brick', value: 1900 },
  { label: 'Sand', value: 1600 },
  { label: 'Gravel', value: 1700 },
  { label: 'Water', value: 1000 },
];

// ─── Formatting ─────────────────────────────────────────────────────────────────

/** Short number for formulas and labels: 6 significant figures, no trailing zeros. */
export const fmt = (n) => {
  if (!Number.isFinite(n)) return '0';
  if (n === 0) return '0';
  return String(Number(n.toPrecision(6)));
};

const num = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
};
const pos = (v) => { const n = num(v); return Number.isFinite(n) && n > 0 ? n : NaN; };

/** A calculator row from raw cell strings (the modal evaluates them). Blank cells count as 1 there. */
const row = (description, cells) => ({
  description,
  count: cells.count != null ? String(cells.count) : '',
  length: cells.length != null ? String(cells.length) : '',
  width: cells.width != null ? String(cells.width) : '',
  height: cells.height != null ? String(cells.height) : '',
  factor: cells.factor != null ? String(cells.factor) : '',
});

const fail = (error) => ({ ok: false, error });
const done = (value, rows, words) => ({ ok: true, value, rows, words });

// ─── Cell formulas with units ───────────────────────────────────────────────────

export { evaluateCell };

/** True when a cell holds working rather than a plain number (so the UI can show its value). */
export const isWorking = (raw) => {
  const s = String(raw ?? '').trim();
  return !!s && !/^-?\d*\.?\d+(e-?\d+)?$/i.test(s);
};

// ─── Convert tab ────────────────────────────────────────────────────────────────

/** Known mass per composite unit → the material's unit, through its density (kg per material unit). */
export function massToUnit({ amount, massUnit = 'kg', density, label }, materialUnit) {
  const a = pos(amount); const d = pos(density);
  const info = unitInfo(materialUnit);
  if (!info) return fail('This line item has no unit to convert into.');
  if (!Number.isFinite(a)) return fail('Enter the mass.');
  if (!Number.isFinite(d)) return fail(`Enter the density (kg per ${info.label}).`);
  const toKg = massUnit === 't' ? 1000 : 1;
  const value = (a * toKg) / d;
  const formula = toKg === 1 ? `=${fmt(a)}/${fmt(d)}` : `=${fmt(a)}*1000/${fmt(d)}`;
  const words = `${label ? `${label}: ` : ''}${fmt(a)} ${massUnit} ÷ ${fmt(d)} kg/${info.label}`;
  return done(value, [row(words, { count: 1, factor: formula })], words);
}

/** Litres per composite unit (× coats) → m³. Only meaningful for a volume material. */
export function litresToVolume({ litres, coats = 1, label }, materialUnit) {
  if (unitKind(materialUnit) !== 'volume') return fail('Litres convert to m³ — this line item is not measured in m³.');
  const l = pos(litres); const c = pos(coats) || 1;
  if (!Number.isFinite(l)) return fail('Enter the litres.');
  const value = (l * c) / 1000;
  const formula = c === 1 ? `=${fmt(l)}/1000` : `=${fmt(l)}*${fmt(c)}/1000`;
  const words = `${label ? `${label}: ` : ''}${fmt(l)} L${c !== 1 ? ` × ${fmt(c)} coats` : ''} ÷ 1000`;
  return done(value, [row(words, { count: 1, factor: formula })], words);
}

/** Items per 1 m² from an item's face size and its joint (bricks, blocks, tiles, batts, panels). */
export function countFromSize({ lengthMm, heightMm, jointMm = 0, label }) {
  const L = pos(lengthMm); const H = pos(heightMm); const j = num(jointMm) || 0;
  if (!Number.isFinite(L) || !Number.isFinite(H)) return fail('Enter the item length and height.');
  const value = 1 / (((L + j) / 1000) * ((H + j) / 1000));
  const lm = fmt((L + j) / 1000); const hm = fmt((H + j) / 1000);
  const formula = `=1/(${lm}*${hm})`;
  const words = `${label ? `${label}: ` : ''}${fmt(L)} × ${fmt(H)} mm${j ? ` + ${fmt(j)} mm joints` : ''} per m²`;
  return done(value, [row(words, { count: 1, factor: formula })], words);
}

/** A percentage of the composite (e.g. rebar 1.5 % by volume). */
export function percentage({ pct, label }) {
  const p = num(pct);
  if (!Number.isFinite(p) || p <= 0) return fail('Enter the percentage.');
  const words = `${label ? `${label}: ` : ''}${fmt(p)} %`;
  return done(p / 100, [row(words, { count: 1, factor: `=${fmt(p)}/100` })], words);
}

/** Waste / allowance: a multiplier for EVERY existing row (not a new row). */
export function wasteMultiplier({ pct }) {
  const p = num(pct);
  if (!Number.isFinite(p) || p === 0) return fail('Enter the allowance %.');
  return { ok: true, value: 1 + p / 100, multiplier: `(1+${fmt(p)}/100)`, words: `${fmt(p)} % allowance` };
}

/** Wrap a row's Factor with a multiplier, keeping what was there ("" counts as 1). */
export function multiplyFactor(factorRaw, multiplier) {
  const f = String(factorRaw ?? '').trim();
  const inner = f.startsWith('=') ? f.slice(1) : f;
  return inner ? `=(${inner})*${multiplier}` : `=${multiplier}`;
}

/** A free formula with units, converted to the material's unit — refused when the dimensions differ. */
export function unitFormula({ expression, label }, materialUnit) {
  const expr = String(expression || '').trim().replace(/^=/, '');
  if (!expr) return fail('Type a formula, e.g. 120 kg / (7850 kg/m^3).');
  const info = unitInfo(materialUnit);
  let r;
  try { r = evaluate(expr); } catch (e) { return fail(`Can't read that formula: ${e.message}`); }
  let value;
  if (isUnit(r)) {
    if (!info || !info.mathjs) return fail(`That has units, but this line item is measured in ${info ? info.label : 'an unknown unit'} — use a plain number.`);
    if (!r.equalBase(mathUnit(info.mathjs))) return fail(`That works out in ${r.formatUnits() || 'other units'}, which doesn't convert to ${info.label}.`);
    value = r.toNumber(info.mathjs);
  } else {
    value = Number(r);
    if (!Number.isFinite(value)) return fail("That doesn't work out to a number.");
  }
  const words = `${label ? `${label}: ` : ''}${expr}`;
  return done(value, [row(words, { count: 1, factor: `=${expr}` })], words);
}

// ─── Geometry tab ───────────────────────────────────────────────────────────────
// Dimensions in mm (as on drawings), member lengths in m. Each shape knows its cross-section area
// (→ volume), its coated perimeter (→ surface area), and so on; `measure` picks which one the rows give.

const mm = (v) => pos(v) / 1000;

export const MEASURES = {
  volume: 'Volume (m³)',
  area: 'Surface area (m²)',
  length: 'Length (m)',
  count: 'Number (No.)',
};

/** I-section (UB / UC / WB / WC; a PFC to within its root radii): d, bf, tf, tw in mm. */
export function iSectionProps({ d, bf, tf, tw }) {
  const D = mm(d); const B = mm(bf); const T = mm(tf); const W = mm(tw);
  if (![D, B, T, W].every(Number.isFinite) || 2 * T >= D) return null;
  return {
    area: 2 * B * T + (D - 2 * T) * W,        // m² of steel in the section
    perimeter: 2 * D + 4 * B - 2 * W,          // m² of coated surface per m (root radii ignored)
    areaFormula: `2*${fmt(B)}*${fmt(T)}+(${fmt(D)}-2*${fmt(T)})*${fmt(W)}`,
    perimeterFormula: `2*${fmt(D)}+4*${fmt(B)}-2*${fmt(W)}`,
  };
}

/** RHS / SHS: b, d, t in mm (corner radii ignored). */
export function rhsProps({ b, d, t }) {
  const B = mm(b); const D = mm(d); const T = mm(t);
  if (![B, D, T].every(Number.isFinite) || 2 * T >= Math.min(B, D)) return null;
  return {
    area: B * D - (B - 2 * T) * (D - 2 * T),
    perimeter: 2 * (B + D),
    areaFormula: `${fmt(B)}*${fmt(D)}-(${fmt(B)}-2*${fmt(T)})*(${fmt(D)}-2*${fmt(T)})`,
    perimeterFormula: `2*(${fmt(B)}+${fmt(D)})`,
  };
}

/** CHS: outside diameter and wall thickness in mm. */
export function chsProps({ od, t }) {
  const D = mm(od); const T = mm(t);
  if (![D, T].every(Number.isFinite) || 2 * T >= D) return null;
  return {
    area: (Math.PI / 4) * (D * D - (D - 2 * T) ** 2),
    perimeter: Math.PI * D,
    areaFormula: `pi/4*(${fmt(D)}^2-(${fmt(D)}-2*${fmt(T)})^2)`,
    perimeterFormula: `pi*${fmt(D)}`,
  };
}

/** Equal / unequal angle: legs a, b and thickness t in mm. */
export function angleProps({ a, b, t }) {
  const A = mm(a); const B = mm(b); const T = mm(t);
  if (![A, B, T].every(Number.isFinite) || T >= Math.min(A, B)) return null;
  return {
    area: T * (A + B - T),
    perimeter: 2 * (A + B),
    areaFormula: `${fmt(T)}*(${fmt(A)}+${fmt(B)}-${fmt(T)})`,
    perimeterFormula: `2*(${fmt(A)}+${fmt(B)})`,
  };
}

const SECTION_PROPS = { isection: iSectionProps, rhs: rhsProps, chs: chsProps, angle: angleProps };

/** How often the part occurs per 1 composite unit: "N per unit", or "1 every X units". */
export function spreadFactor(spread) {
  if (!spread || spread.mode === 'per') {
    const n = pos(spread?.n ?? 1);
    return Number.isFinite(n) && n !== 1 ? fmt(n) : '';
  }
  const x = pos(spread.every);
  return Number.isFinite(x) && x !== 1 ? `1/${fmt(x)}` : '';
}

const withSpread = (r, spread) => {
  const s = spreadFactor(spread);
  if (!s) return r;
  return { ...r, factor: r.factor ? `=(${String(r.factor).replace(/^=/, '')})*${s}` : `=${s}` };
};

const totalOf = (rows) => rows.reduce((sum, r) => {
  const cells = ['count', 'length', 'width', 'height', 'factor'].map(f => r[f]);
  if (!cells.some(c => String(c ?? '').trim() !== '')) return sum;
  return sum + cells.reduce((p, c) => p * (String(c ?? '').trim() === '' ? 1 : (evaluateCell(c) || 1)), 1);
}, 0);

/** A steel member (I / RHS / CHS / angle) of `count` × `lengthM` — volume, coated area or length. */
export function memberRows({ shape, dims, count, lengthM, name, measure, spread }) {
  const props = SECTION_PROPS[shape]?.(dims || {});
  const n = pos(count) || 1; const L = pos(lengthM);
  if (!props) return fail('Check the section dimensions.');
  if (!Number.isFinite(L) && measure !== 'count') return fail('Enter the member length (m).');
  const what = name || 'Member';
  let r;
  if (measure === 'volume') r = row(`${what}: ${fmt(n)} × ${fmt(L)} m × section ${fmt(props.area)} m²`, { count: n, length: L, width: `=${props.areaFormula}` });
  else if (measure === 'area') r = row(`${what}: ${fmt(n)} × ${fmt(L)} m × coated perimeter ${fmt(props.perimeter)} m`, { count: n, length: L, width: `=${props.perimeterFormula}` });
  else if (measure === 'length') r = row(`${what}: ${fmt(n)} × ${fmt(L)} m`, { count: n, length: L });
  else r = row(`${what}: ${fmt(n)} No.`, { count: n });
  const rows = [withSpread(r, spread)];
  return done(totalOf(rows), rows, r.description);
}

/** Plates (base plates, cleats, stiffeners, gussets): count × L × W × t (mm). */
export function plateRows({ plates, measure, bothFaces = true, name, spread }) {
  const list = (plates || []).filter(p => pos(p.l) && pos(p.w) && (measure !== 'volume' || pos(p.t)));
  if (!list.length) return fail('Enter at least one plate (length, width and thickness).');
  const rows = list.map(p => {
    const n = pos(p.count) || 1;
    const label = `${name ? `${name} — ` : ''}${p.label || 'Plate'}: ${fmt(n)} × ${fmt(pos(p.l))} × ${fmt(pos(p.w))}${pos(p.t) ? ` × ${fmt(pos(p.t))}` : ''} mm`;
    if (measure === 'volume') return withSpread(row(label, { count: n, length: mm(p.l), width: mm(p.w), height: mm(p.t) }), spread);
    if (measure === 'area') return withSpread(row(`${label}${bothFaces ? ' (both faces)' : ''}`, { count: n, length: mm(p.l), width: mm(p.w), factor: bothFaces ? 2 : '' }), spread);
    if (measure === 'count') return withSpread(row(label, { count: n }), spread);
    return withSpread(row(label, { count: n, length: mm(p.l) }), spread);
  });
  return done(totalOf(rows), rows, `${rows.length} plate row${rows.length === 1 ? '' : 's'}`);
}

/** A splice: flange plates + web plates (+ bolts when counting). */
export function spliceRows({ flange, web, bolts, measure, bothFaces = true, name = 'Splice', spread }) {
  if (measure === 'count') {
    const sides = pos(bolts?.sides) || 1; const rowsN = pos(bolts?.rows); const cols = pos(bolts?.cols);
    if (!Number.isFinite(rowsN) || !Number.isFinite(cols)) return fail('Enter the bolt rows and columns.');
    const r = withSpread(row(`${name} bolts: ${fmt(sides)} side${sides === 1 ? '' : 's'} × ${fmt(rowsN)} × ${fmt(cols)}`, { count: `=${fmt(sides)}*${fmt(rowsN)}*${fmt(cols)}` }), spread);
    return done(totalOf([r]), [r], r.description);
  }
  const plates = [];
  if (flange && pos(flange.l)) plates.push({ ...flange, label: 'flange plates' });
  if (web && pos(web.l)) plates.push({ ...web, label: 'web plates' });
  return plateRows({ plates, measure, bothFaces, name, spread });
}

/** Concrete: a cylinder (pile, round column) or a rectangular solid (pad, thickening). */
export function concreteRows({ shape, count, diameterMm, lengthMm, widthMm, depthM, measure, name, spread }) {
  const n = pos(count) || 1; const h = pos(depthM);
  if (!Number.isFinite(h)) return fail('Enter the depth / height (m).');
  const what = name || (shape === 'cylinder' ? 'Pile' : 'Pad');
  let r;
  if (shape === 'cylinder') {
    const D = mm(diameterMm);
    if (!Number.isFinite(D)) return fail('Enter the diameter.');
    if (measure === 'volume') r = row(`${what}: ${fmt(n)} × Ø${fmt(pos(diameterMm))} mm × ${fmt(h)} m`, { count: n, length: h, width: `=pi*${fmt(D)}^2/4` });
    else if (measure === 'area') r = row(`${what} formwork: ${fmt(n)} × Ø${fmt(pos(diameterMm))} mm × ${fmt(h)} m`, { count: n, length: h, width: `=pi*${fmt(D)}` });
    else if (measure === 'length') r = row(`${what}: ${fmt(n)} × ${fmt(h)} m`, { count: n, length: h });
    else r = row(`${what}: ${fmt(n)} No.`, { count: n });
  } else {
    const L = mm(lengthMm); const W = mm(widthMm);
    if (!Number.isFinite(L) || !Number.isFinite(W)) return fail('Enter the length and width.');
    if (measure === 'volume') r = row(`${what}: ${fmt(n)} × ${fmt(pos(lengthMm))} × ${fmt(pos(widthMm))} mm × ${fmt(h)} m`, { count: n, length: L, width: W, height: h });
    else if (measure === 'area') r = row(`${what} formwork (sides): ${fmt(n)} × ${fmt(h)} m deep`, { count: n, length: h, width: `=2*(${fmt(L)}+${fmt(W)})` });
    else if (measure === 'length') r = row(`${what}: ${fmt(n)} × ${fmt(L)} m`, { count: n, length: L });
    else r = row(`${what}: ${fmt(n)} No.`, { count: n });
  }
  const rows = [withSpread(r, spread)];
  return done(totalOf(rows), rows, r.description);
}

/** Section mass from its designation's kg/m (310UB40.4 → 40.4) — a cross-check on typed dims. */
export function designationKgPerM(designation) {
  const m = String(designation || '').replace(/\s+/g, '').match(/^\d+(?:UB|UC|WB|WC)(\d+(?:\.\d+)?)$/i);
  return m ? parseFloat(m[1]) : null;
}

export const STEEL_DENSITY = 7850;

/** Preview total of rows exactly as the calculator will total them. */
export const previewTotal = totalOf;
